import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import { useCosmeticCatalogues, CosmeticPicker } from '../../../components/staff/CosmeticPicker';

const CLASS_DATA_TEMPLATES = {
    badge: '{\n  "badgeId": ""\n}',
    profile_skin: '{\n  "skinId": ""\n}',
    circle_bg: '{\n  "circleBgId": ""\n}',
    inventory_item: '{\n  "inventoryItemId": ""\n}',
    profile_bundle: '{\n  "bundleItems": []\n}',
    giveaway_token: '{}',
};

const CLASS_DESCRIPTION_TEMPLATES = {
    profile_skin: 'Unlock the [skinname] profile skin for your profile!',
    circle_bg: '[PFP Background] This will give you a profile picture background image instead of a colour!',
    profile_bundle: '[Bundle] This bundle contains [placeholder]!',
};

// Maps item class to the StampShack CATEGORIES sortOrder number
const CLASS_TO_CATEGORY = {
    badge: 1,
    giveaway_token: 2,
    inventory_item: 3,
    circle_bg: 4,
    profile_skin: 5,
    profile_bundle: 6,
};

const computeSortOrder = (cls, featured) => {
    const cat = CLASS_TO_CATEGORY[cls] || 0;
    return featured ? (9990 + cat) : cat;
};

const SHOP_CATEGORIES = [
    { sortOrder: 1, name: 'Achievements' },
    { sortOrder: 2, name: 'Competitions' },
    { sortOrder: 3, name: 'Habbo Skills' },
    { sortOrder: 4, name: 'PFP Backgrounds' },
    { sortOrder: 5, name: 'Profile Skins' },
    { sortOrder: 6, name: 'Bundles' },
];

const getCategoryName = (sortOrder) => {
    const s = String(sortOrder);
    const effectiveOrder = s.startsWith('999') ? parseInt(s.slice(3), 10) : parseInt(s, 10);
    return SHOP_CATEGORIES.find(c => c.sortOrder === effectiveOrder)?.name || 'Uncategorised';
};

const ITEM_CLASSES = [
    { value: 'badge', label: 'Badge' },
    { value: 'circle_bg', label: 'PFP Background' },
    { value: 'giveaway_token', label: 'Giveaway Token' },
    { value: 'inventory_item', label: 'Inventory Item' },
    { value: 'profile_bundle', label: 'Profile Bundle' },
    { value: 'profile_skin', label: 'Profile Skin' },
];

const BUNDLE_CLASS_ID_KEY = {
    badge: 'badgeId',
    profile_skin: 'skinId',
    circle_bg: 'circleBgId',
};

// Structured field configs per class
const CLASS_FIELD_CONFIG = {
    badge:          { key: 'badgeId',         label: 'Badge ID',          placeholder: 'e.g. OG1' },
    profile_skin:   { key: 'skinId',          label: 'Skin ID',           placeholder: 'e.g. steam' },
    circle_bg:      { key: 'circleBgId',      label: 'Circle BG ID',      placeholder: 'e.g. CLD' },
    inventory_item: { key: 'inventoryItemId', label: 'Inventory Item ID', placeholder: 'e.g. inv_item_123' },
};

function ShopItems({ userData }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showItemModal, setShowItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    useEffect(() => {
        loadItems();
    }, []);

    const loadItems = async () => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}shop/admin/items`, {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) setItems(data.items);
        } catch (error) {
            console.error('Error loading items:', error);
        } finally {
            setLoading(false);
        }
    };

    const deleteItem = async (itemId) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}shop/admin/items/${itemId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Item deleted successfully');
                loadItems();
            }
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    return (
        <AdminLayout>
            <ContentBox theme="default" tint="maroon" header="Shop Items">
                <div className="flex justify-between items-center mb-4">
                    <p className="text-gray-700">Manage shop items, stock, and pricing.</p>
                    <button
                        onClick={() => {
                            setEditingItem(null);
                            setShowItemModal(true);
                        }}
                        className="bg-maroon hover:opacity-90 text-white px-4 py-2 font-['Jersey_10'] uppercase text-[14px] tracking-[1px]"
                        style={{ backgroundColor: '#5A2929', boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                    >
                        Add Item
                    </button>
                </div>

                {loading ? (
                    <p>Loading...</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                            <tr className="bg-gray-100">
                                <th className="border border-gray-300 px-2 py-2 w-10"></th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Name</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Class</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Cost</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Stock</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Active</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {(() => {
                                const rows = [];
                                let lastCategory = null;
                                for (const item of items) {
                                    const cat = getCategoryName(item.SortOrder);
                                    if (cat !== lastCategory) {
                                        lastCategory = cat;
                                        rows.push(
                                            <tr key={`cat-${cat}`} className="bg-gray-200">
                                                <td colSpan={7} className="border border-gray-300 px-4 py-1 font-['Jersey_10'] uppercase text-[13px] tracking-widest text-gray-600">
                                                    {cat}
                                                </td>
                                            </tr>
                                        );
                                    }
                                    const featured = String(item.SortOrder).startsWith('999');
                                    const rowBg = featured ? 'bg-yellow-50 hover:bg-yellow-100' : 'hover:bg-gray-50';
                                    rows.push(
                                <tr key={item.ItemID} className={rowBg}>
                                    <td className="border border-gray-300 px-2 py-1">
                                        <img
                                            src={item.ImageURL || '/img/Frank_05.png'}
                                            alt=""
                                            className="w-8 h-8 object-contain"
                                            style={{ imageRendering: 'pixelated' }}
                                        />
                                    </td>
                                    <td className="border border-gray-300 px-4 py-2">
                                        {featured && <span className="text-yellow-600 mr-1">★</span>}
                                        {item.ItemName}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-2">{item.ItemClass}</td>
                                    <td className="border border-gray-300 px-4 py-2">{item.BaseStampCost}</td>
                                    <td className="border border-gray-300 px-4 py-2">
                                        {item.GlobalStock ? `${item.GlobalStock} global` : `${item.StockPerUser}/user`}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-2">
                                        {item.IsActive ? 'Yes' : 'No'}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-2">
                                        <button
                                            onClick={() => {
                                                setEditingItem(item);
                                                setShowItemModal(true);
                                            }}
                                            className="text-blue-600 hover:underline mr-3"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => deleteItem(item.ItemID)}
                                            className="text-red-600 hover:underline"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                                    );
                                }
                                return rows;
                            })()}
                            </tbody>
                        </table>
                    </div>
                )}

                {showItemModal && (
                    <ItemModal
                        item={editingItem}
                        onClose={() => setShowItemModal(false)}
                        onSave={() => {
                            setShowItemModal(false);
                            loadItems();
                        }}
                    />
                )}
            </ContentBox>
        </AdminLayout>
    );
}

// ── Structured class data editors ────────────────────────────────────────────

function BundleBuilder({ classData, onChange, catalogues }) {
    const items = (() => {
        try {
            const parsed = JSON.parse(classData || '{}');
            return Array.isArray(parsed.bundleItems) ? parsed.bundleItems : [];
        } catch {
            return [];
        }
    })();

    const syncItems = (newItems) => {
        onChange(JSON.stringify({ bundleItems: newItems }, null, 2));
    };

    const addItem = () => {
        syncItems([...items, { class: 'profile_skin', skinId: '' }]);
    };

    const removeItem = (index) => {
        syncItems(items.filter((_, i) => i !== index));
    };

    const updateItemClass = (index, newClass) => {
        const idKey = BUNDLE_CLASS_ID_KEY[newClass];
        syncItems(items.map((item, i) => i !== index ? item : { class: newClass, [idKey]: '' }));
    };

    const updateItemId = (index, value) => {
        const idKey = BUNDLE_CLASS_ID_KEY[items[index].class];
        syncItems(items.map((item, i) => i === index ? { ...item, [idKey]: value } : item));
    };

    return (
        <div>
            {items.map((bundleItem, index) => {
                const idKey = BUNDLE_CLASS_ID_KEY[bundleItem.class] || 'id';
                return (
                    <div key={index} className="flex gap-2 mb-2 items-center">
                        <select
                            value={bundleItem.class}
                            onChange={(e) => updateItemClass(index, e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                            <option value="badge">Badge</option>
                            <option value="circle_bg">PFP Background</option>
                            <option value="profile_skin">Profile Skin</option>
                        </select>
                        <CosmeticPicker
                            options={catalogues?.[bundleItem.class] || []}
                            value={bundleItem[idKey] || ''}
                            onChange={(val) => updateItemId(index, val)}
                            placeholder={idKey}
                        />
                        <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:underline text-sm"
                        >
                            Remove
                        </button>
                    </div>
                );
            })}
            <button
                type="button"
                onClick={addItem}
                className="text-blue-600 hover:underline text-sm mt-1"
            >
                + Add Item
            </button>
        </div>
    );
}

function ClassDataEditor({ itemClass, classData, onChange, catalogues, onCosmeticPicked }) {
    const parsed = (() => {
        try { return JSON.parse(classData || '{}'); }
        catch { return {}; }
    })();

    const updateField = (key, value) => {
        onChange(JSON.stringify({ ...parsed, [key]: value }, null, 2));
    };

    const config = CLASS_FIELD_CONFIG[itemClass];

    return (
        <div>
            {/* Structured input */}
            {itemClass === 'profile_bundle' ? (
                <BundleBuilder classData={classData} onChange={onChange} catalogues={catalogues} />
            ) : itemClass === 'giveaway_token' ? (
                <p className="text-sm text-gray-400 italic py-1">No additional data required for this class.</p>
            ) : config ? (
                <div className="flex items-center gap-3 mb-1">
                    <label className="text-sm text-gray-600 font-['Jersey_10'] uppercase tracking-wide w-36 flex-shrink-0">
                        {config.label}
                    </label>
                    {catalogues?.[itemClass] ? (
                        <CosmeticPicker
                            options={catalogues[itemClass]}
                            value={parsed[config.key] || ''}
                            onChange={(val) => updateField(config.key, val)}
                            onPick={onCosmeticPicked}
                            placeholder={config.placeholder}
                        />
                    ) : (
                        <input
                            type="text"
                            value={parsed[config.key] || ''}
                            onChange={(e) => updateField(config.key, e.target.value)}
                            placeholder={config.placeholder}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded font-mono text-sm"
                        />
                    )}
                </div>
            ) : null}

            {/* JSON debug — always visible below structured UI */}
            <details className="mt-2">
                <summary className="text-xs text-gray-400 cursor-pointer select-none hover:text-gray-600">
                    JSON Debug
                </summary>
                <textarea
                    value={classData}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full mt-1 px-2 py-1 border border-gray-200 rounded font-mono text-xs text-gray-500 bg-gray-50"
                    rows="3"
                    spellCheck={false}
                />
            </details>
        </div>
    );
}

// ── Item modal ────────────────────────────────────────────────────────────────

function ItemModal({ item, onClose, onSave }) {
    const catalogues = useCosmeticCatalogues();

    const initialClass = item?.ItemClass || 'inventory_item';
    const initialFeatured = item ? String(item.SortOrder || 0).startsWith('999') : false;
    const initialClassData = item?.ClassData
        ? JSON.stringify(JSON.parse(item.ClassData), null, 2)
        : CLASS_DATA_TEMPLATES[initialClass] || '{}';
    const initialDescription = item?.ItemDescription || CLASS_DESCRIPTION_TEMPLATES[initialClass] || '';

    const [formData, setFormData] = useState({
        itemName: item?.ItemName || '',
        itemDescription: initialDescription,
        itemClass: initialClass,
        classData: initialClassData,
        baseStampCost: item?.BaseStampCost || 1,
        imageURL: item?.ImageURL || '',
        stockPerUser: item?.StockPerUser || 1,
        globalStock: item?.GlobalStock || '',
        isActive: item?.IsActive !== undefined ? item.IsActive : 1,
        isFeatured: initialFeatured,
        sortOrder: item?.SortOrder ?? computeSortOrder(initialClass, initialFeatured),
    });

    // Picking a cosmetic fills the shop tile image in for you. Anything you've
    // typed yourself is left alone — only an empty field or a previous auto-fill
    // gets replaced, so re-picking still updates but never eats a manual edit.
    const autoFilledImageRef = useRef('');

    const applyCosmeticDefaults = (option) => {
        const image = option?.shopImage || option?.image || '';
        if (!image) return;

        setFormData(prev => {
            const current = String(prev.imageURL || '').trim();
            if (current && current !== autoFilledImageRef.current) return prev;
            autoFilledImageRef.current = image;
            return { ...prev, imageURL: image };
        });
    };


    const handleClassChange = (newClass) => {
        const isClassDataEmpty = formData.classData.trim() === '' || formData.classData.trim() === '{}';
        const isDescAutoFilled = formData.itemDescription.trim() === '' ||
            Object.values(CLASS_DESCRIPTION_TEMPLATES).includes(formData.itemDescription);
        setFormData({
            ...formData,
            itemClass: newClass,
            classData: isClassDataEmpty ? (CLASS_DATA_TEMPLATES[newClass] || '{}') : formData.classData,
            itemDescription: isDescAutoFilled ? (CLASS_DESCRIPTION_TEMPLATES[newClass] || '') : formData.itemDescription,
            sortOrder: computeSortOrder(newClass, formData.isFeatured),
        });
    };

    const handleFeaturedChange = (featured) => {
        setFormData({
            ...formData,
            isFeatured: featured,
            sortOrder: computeSortOrder(formData.itemClass, featured),
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const url = item
                ? `${apiUrl}shop/admin/items/${item.ItemID}`
                : `${apiUrl}shop/admin/items`;

            const { isFeatured, ...submitData } = formData;
            const body = {
                ...submitData,
                classData: formData.classData ? JSON.parse(formData.classData) : null,
                globalStock: formData.globalStock ? parseInt(formData.globalStock) : null
            };

            const response = await fetch(url, {
                method: item ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body)
            });

            const data = await response.json();
            if (data.success) {
                toast.success(data.message);
                onSave();
            }
        } catch (error) {
            console.error('Error saving item:', error);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
            <div className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-2xl w-full mx-4 z-[201] max-h-[90vh] overflow-y-auto">
                <h2 className="text-maroon uppercase font-['Jersey_10'] text-[20px] tracking-[1px] mb-4">
                    {item ? 'Edit Item' : 'Add Item'}
                </h2>
                <hr className="border-gold mb-4" />

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-2 gap-4 mb-4">

                        <div className="col-span-2">
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Item Name
                            </label>
                            <input
                                type="text"
                                value={formData.itemName}
                                onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                required
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Description
                            </label>
                            <textarea
                                value={formData.itemDescription}
                                onChange={(e) => setFormData({ ...formData, itemDescription: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                rows="3"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Item Class
                            </label>
                            <select
                                value={formData.itemClass}
                                onChange={(e) => handleClassChange(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                            >
                                {ITEM_CLASSES.map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Base Cost (cards)
                            </label>
                            <input
                                type="number"
                                value={formData.baseStampCost}
                                onChange={(e) => setFormData({ ...formData, baseStampCost: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                min="0"
                                required
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                {formData.itemClass === 'profile_bundle' ? 'Bundle Items' : 'Class Data'}
                            </label>
                            <ClassDataEditor
                                itemClass={formData.itemClass}
                                classData={formData.classData}
                                catalogues={catalogues}
                                onCosmeticPicked={applyCosmeticDefaults}
                                onChange={(val) => setFormData({ ...formData, classData: val })}
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Image URL
                            </label>
                            <div className="flex gap-3 items-center">
                                <input
                                    type="text"
                                    value={formData.imageURL}
                                    onChange={(e) => setFormData({ ...formData, imageURL: e.target.value })}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded"
                                    placeholder="/img/Frank_05.png"
                                />
                                <img
                                    src={formData.imageURL || '/img/Frank_05.png'}
                                    alt="Preview"
                                    className="w-16 h-16 border border-gray-300 object-contain bg-white flex-shrink-0"
                                    style={{ imageRendering: 'pixelated' }}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Stock Per User
                            </label>
                            <input
                                type="number"
                                value={formData.stockPerUser}
                                onChange={(e) => setFormData({ ...formData, stockPerUser: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                min="1"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Global Stock (optional)
                            </label>
                            <input
                                type="number"
                                value={formData.globalStock}
                                onChange={(e) => setFormData({ ...formData, globalStock: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                min="0"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Active
                            </label>
                            <select
                                value={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                            >
                                <option value={1}>Yes</option>
                                <option value={0}>No</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-3 pt-6">
                            <input
                                type="checkbox"
                                id="isFeatured"
                                checked={formData.isFeatured}
                                onChange={(e) => handleFeaturedChange(e.target.checked)}
                                className="w-4 h-4 accent-maroon"
                            />
                            <label htmlFor="isFeatured" className="text-gray-700 font-['Jersey_10'] uppercase text-[14px] cursor-pointer select-none">
                                Featured
                            </label>
                        </div>

                        <div className="col-span-2">
                            <details>
                                <summary className="text-xs text-gray-400 cursor-pointer select-none hover:text-gray-600 font-['Jersey_10'] uppercase tracking-wide">
                                    Category Debug
                                </summary>
                                <div className="flex items-center gap-2 mt-1">
                                    <input
                                        type="number"
                                        value={formData.sortOrder}
                                        onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                                        className="w-28 px-2 py-1 border border-gray-200 rounded text-xs font-mono text-gray-500 bg-gray-50"
                                    />
                                    <span className="text-xs text-gray-400">
                                        (auto: {computeSortOrder(formData.itemClass, formData.isFeatured)})
                                    </span>
                                </div>
                            </details>
                        </div>

                    </div>

                    <div className="flex gap-3 justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[14px]"
                            style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bg-maroon hover:opacity-90 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[14px]"
                            style={{ backgroundColor: '#5A2929', boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                        >
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ShopItems;
